// utils/mathText.ts
//
// Single source of truth for turning LLM text into markdown that remark-math +
// KaTeX render correctly. Every component that shows AI-generated text (chat,
// notes, quiz questions/options, results, feedback) goes through
// `normalizeMathMarkdown`, so a new screen can't regress to showing raw
// "\(\sqrt{50}\)" again.
//
// It handles the three ways LLM math reaches the UI broken:
//   1. LaTeX delimiters remark-math doesn't understand: \( \) and \[ \].
//   2. JSON escape damage: a model writing "\frac" / "\times" / "\beta" inside a
//      JSON string yields a form-feed + "rac", a tab + "imes", a backspace +
//      "eta"... The command is silently lost unless we put the backslash back.
//   3. Currency: "$5 and $10" would otherwise render as math "5 and ".
// Code spans and fenced code blocks are left untouched.

/**
 * Restores LaTeX commands whose leading "\x" was decoded as a JSON control
 * character. Only applied where the following letters form a LaTeX command, so
 * genuine newlines/tabs in prose are kept.
 */
const CONTROL_CHAR_REPAIRS: Array<[RegExp, string]> = [
  [/\f(?=[a-zA-Z])/g, '\\f'], // \frac \forall
  [/\x08(?=[a-zA-Z])/g, '\\b'], // \beta \bar \binom \because
  [/\v(?=[a-zA-Z])/g, '\\v'], // \vec \varphi \vee
  [/\r(?=[a-zA-Z])/g, '\\r'], // \right \rho \rangle
  [/\t(?=(?:imes|heta|ext(?:bf|it|rm)?|an|au|ilde|riangle|op|o|frac|herefore)(?![a-zA-Z]))/g, '\\t'],
  [/\n(?=(?:eq|abla|otin|leq|geq|mid|parallel|subseteq)(?![a-zA-Z]))/g, '\\n'],
];

export function repairLatexControlChars(text: string): string {
  let out = text;
  for (const [pattern, replacement] of CONTROL_CHAR_REPAIRS) {
    out = out.replace(pattern, replacement);
  }
  return out;
}

// Fenced blocks (``` / ~~~) and inline code spans: never rewrite inside these.
const CODE_SEGMENT = /(```[\s\S]*?(?:```|$)|~~~[\s\S]*?(?:~~~|$)|`[^`\n]*`)/g;

// One pass over prose, longest delimiters first:
//   \[...\]   $$...$$   \(...\)   $...$
// Single-$ math follows the Pandoc rule: no whitespace just inside the
// delimiters and no digit right after the closing $ — so prices stay text.
const MATH_SPAN =
  /\\\[([\s\S]+?)\\\]|(?<!\\)\$\$([\s\S]+?)\$\$|\\\(([\s\S]+?)\\\)|(?<![\\$])\$(?![\s$])((?:\\\$|[^$\n])+?)(?<![\s\\])\$(?![\d$])/g;

export interface NormalizeOptions {
  /**
   * Inline contexts (buttons, list items, single-line labels) keep display math
   * on the same line; block contexts put $$ on their own lines so KaTeX renders
   * a centred display equation.
   */
  inline?: boolean;
}

function normalizeProse(prose: string, inline: boolean): string {
  let out = '';
  let last = 0;
  MATH_SPAN.lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = MATH_SPAN.exec(prose)) !== null) {
    // Any "$" left in plain text is a literal dollar sign, not math.
    out += escapeLooseDollars(prose.slice(last, match.index));

    const display = match[1] ?? match[2];
    if (display !== undefined) {
      const tex = display.trim();
      out += inline ? `$$${tex}$$` : `\n$$\n${tex}\n$$\n`;
    } else {
      out += `$${(match[3] ?? match[4] ?? '').trim()}$`;
    }
    last = match.index + match[0].length;
  }

  return out + escapeLooseDollars(prose.slice(last));
}

function escapeLooseDollars(text: string): string {
  return text.replace(/(?<!\\)\$/g, '\\$');
}

/**
 * Converts LLM output into markdown with only `$...$` / `$$...$$` math, which
 * is what remark-math parses. Safe to call on any string, including partial
 * streamed content and plain text with no math at all.
 */
export function normalizeMathMarkdown(
  text: string | null | undefined,
  options: NormalizeOptions = {},
): string {
  if (!text) return '';
  const inline = Boolean(options.inline);

  return text
    .split(CODE_SEGMENT)
    .map((segment, index) =>
      index % 2 === 1 ? segment : normalizeProse(repairLatexControlChars(segment), inline),
    )
    .join('');
}
